/**
 * MonthEndPopup - the "Time to close the books on [Month]" pop-up on Home.
 *
 * When it shows:
 *   - Normal: the 1st to the 7th of each month, for last month's review, until the
 *     user opens it or skips it.
 *   - Launch: once for everyone after this update ships, for their last finished
 *     month, even if they've already opened that review. Stops after it's viewed or
 *     skipped, or 7 days after it first appeared.
 *   - Only for users who were using the app from the start of that month.
 *
 * Buttons: "Let's see it" opens the review. "Maybe later" hides it until the app
 * is next opened. "Skip this month" hides it until next month (the review stays in
 * 2026 Stats either way).
 */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronRight } from "lucide-react";
import customToast from "../../utils/customToast";
import MonthEndReview from "./MonthEndReview";
import {
  getReviewPalette, getReviewState, isReviewEligible, pinNoteToMonth,
  monthKeyOf, monthNameOf, shiftMonthKey,
} from "../../utils/monthEndReview";
import { usePlan, PLAN_REVIEW } from "../../utils/plan"; // plan_08

const SERIF = "'DM Serif Display', serif";
const SANS = "'DM Sans', sans-serif";
const INK = '#2E2C33';
const SHADOW = '0 12px 30px rgba(28,27,31,0.08), 0 2px 6px rgba(28,27,31,0.04)';
const LAUNCH_DAYS = 7;

// "Maybe later" lasts until the app is next opened. Kept outside the component so
// switching tabs doesn't bring it straight back; reset once the app has been in
// the background for a while.
let laterUntilReopen = false;
let hiddenSince = null;
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince = Date.now();
    } else if (hiddenSince && Date.now() - hiddenSince > 5 * 60 * 1000) {
      laterUntilReopen = false;
      hiddenSince = null;
    }
  });
}

const localDay = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const daysSince = (day) => {
  if (!day) return 0;
  const [y, m, d] = day.split('-').map(Number);
  return Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(y, m - 1, d).getTime()) / 86400000);
};

const MonthEndPopup = ({
  userData,
  saveUserData,
  transactions = [],
  noSpendDays = {},
  sinkingFunds = [],
  categoryBudgets = {},
  theme = 'pink',
  suppressed = false,
  onShowingChange,
}) => {
  const [hidden, setHidden] = useState(laterUntilReopen);
  const [reviewOpen, setReviewOpen] = useState(false);
  const stamped = useRef(false);
  const plan = usePlan();
  const P = plan ? PLAN_REVIEW : getReviewPalette(theme);

  const now = new Date();
  const monthKey = shiftMonthKey(monthKeyOf(now), -1);
  const s = getReviewState(userData);
  const launch = !s.launch_v1 && daysSince(s.launch_first_shown) < LAUNCH_DAYS;
  const normal = now.getDate() <= 7 && !(s.seen && s.seen[monthKey]) && !(s.skipped && s.skipped[monthKey]);
  const eligible = !!userData && isReviewEligible(transactions, monthKey);
  const show = eligible && (launch || normal) && !suppressed;

  // Remember when the launch pop-up first appeared, so it can stop after 7 days.
  useEffect(() => {
    if (show && !hidden && launch && !s.launch_first_shown && !stamped.current) {
      stamped.current = true;
      saveUserData({ month_end_review: { ...s, launch_first_shown: localDay() } });
    }
  }, [show, hidden, launch, s, saveUserData]);

  // Tells Home when this pop-up (or the review) is on screen, so other
  // pop-ups can wait their turn.
  const showing = reviewOpen || (show && !hidden);
  useEffect(() => {
    if (onShowingChange) onShowingChange(showing);
  }, [showing, onShowingChange]);

  const finish = (extra) => {
    const next = { ...s, ...extra };
    if (launch) next.launch_v1 = true;
    saveUserData({ month_end_review: next });
  };

  const onView = () => {
    finish({ seen: { ...(s.seen || {}), [monthKey]: new Date().toISOString() } });
    setReviewOpen(true);
  };
  const onLater = () => {
    laterUntilReopen = true;
    setHidden(true);
  };
  const onSkip = () => {
    finish({ skipped: { ...(s.skipped || {}), [monthKey]: true } });
    setHidden(true);
  };

  if (reviewOpen) {
    return (
      <MonthEndReview
        monthKey={monthKey}
        transactions={transactions}
        noSpendDays={noSpendDays}
        sinkingFunds={sinkingFunds}
        categoryBudgets={categoryBudgets}
        theme={theme}
        onClose={() => { setReviewOpen(false); setHidden(true); }}
        onPinNote={(text, nextKey) => {
          pinNoteToMonth(userData, saveUserData, nextKey, text);
          customToast.success('Pinned!', `Added to ${monthNameOf(nextKey)}’s budget notes`);
        }}
      />
    );
  }

  if (!show || hidden) return null;

  const teasers = [
    { role: 'income', rot: -7, top: 14, left: 0, emoji: '\u{1F4B0}', label: 'Came in', blur: '$4,000', z: 1 },
    { role: 'saved', rot: 5, top: 4, left: 150, emoji: '\u{1F331}', label: 'You kept', blur: '$1,000', z: 2 },
    { role: 'leftover', rot: -2, top: 80, left: 76, emoji: '\u{1F31E}', label: 'No-spend days', blur: '12', z: 3 },
  ];

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-5" style={{ zIndex: 9999 }}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mer-popup-title"
        className={"relative w-full max-w-sm animate-in fade-in zoom-in duration-200" + (plan ? " wy-plan-review" : "")}
        style={{ borderRadius: 32, padding: '28px 26px 18px', background: `linear-gradient(180deg, ${P.ui.bgTop} 0%, #FFFFFF 70%)`, boxShadow: '0 30px 60px rgba(28,27,31,0.22)', fontFamily: SANS }}
      >
        <div style={{ position: 'relative', height: 190, width: 290, margin: '0 auto' }}>
          {teasers.map((c) => (
            <div key={c.label} style={{ position: 'absolute', top: c.top, left: c.left, width: 136, boxSizing: 'border-box', padding: '12px 12px 14px', borderRadius: 18, background: P[c.role].fill, boxShadow: SHADOW, transform: `rotate(${c.rot}deg)`, zIndex: c.z }}>
              <span style={{ display: 'flex', width: 30, height: 30, borderRadius: 10, background: 'rgba(255,255,255,0.65)', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>{c.emoji}</span>
              <span style={{ display: 'block', marginTop: 8, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: P[c.role].label }}>{c.label}</span>
              <span aria-hidden="true" style={{ display: 'block', marginTop: 2, fontFamily: SERIF, fontSize: 21, lineHeight: 1.1, color: P[c.role].deep, filter: 'blur(5px)' }}>{c.blur}</span>
            </div>
          ))}
        </div>

        {launch && (
          <span style={{ display: 'inline-block', marginTop: 10, padding: '3px 10px', borderRadius: 10, background: P.income.fill, color: P.income.label, fontSize: 12, fontWeight: 700 }}>New</span>
        )}
        <h2 id="mer-popup-title" style={{ margin: `${launch ? 10 : 14}px 0 0`, fontFamily: SERIF, fontWeight: 400, fontSize: 32, lineHeight: 1.08, letterSpacing: '-0.015em', color: P.income.deep }}>
          Time to close the books on {monthNameOf(monthKey)}
        </h2>
        <p style={{ margin: '12px 0 0', fontSize: 16, lineHeight: 1.5, color: INK }}>
          Your month-end review is ready! See what you kept, what you spent and how you did overall. It only takes a minute!
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 26 }}>
          <button
            type="button"
            onClick={onView}
            style={{ width: '100%', padding: '18px 24px', border: 0, borderRadius: 22, background: P.ui.button, boxShadow: P.ui.shadow, color: '#FFFFFF', fontSize: 17, fontWeight: 600, fontFamily: SANS, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            Let&rsquo;s see it
            <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={onLater}
            style={{ width: '100%', padding: '16px 24px', border: `1.5px solid ${P.ui.button}`, borderRadius: 22, background: 'transparent', color: P.ui.button, fontSize: 16, fontWeight: 600, fontFamily: SANS }}
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={onSkip}
            style={{ width: '100%', minHeight: 44, border: 0, background: 'transparent', color: '#8a8a8a', fontSize: 14, fontWeight: 500, fontFamily: SANS }}
          >
            Skip this month
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MonthEndPopup;
